import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useFrame, type ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { useSceneState } from '../providers/SceneProvider';
import { useCuboidHover } from '../../application/hooks/useCuboidHover';
import {
  buildFaceInstances,
  buildEdgeInstances,
  updateInstanceMatrices,
  getInstanceUuid,
  disposeSharedGeometries,
} from '../../infrastructure/three/CuboidInstanceFactory';
import {
  createCuboidFaceMaterial,
  createCuboidEdgeMaterial,
} from '../../infrastructure/three/MaterialFactory';
import { interpolate, resolveTransition } from '../../domain/services/CuboidInterpolator';
import type { Cuboid } from '../../domain/entities/Cuboid';

export function CuboidGroup() {
  const { currentFrame, prevFrame } = useSceneState();
  const { onHover, onUnhover, onPin } = useCuboidHover();

  const [faceMesh, setFaceMesh] = useState<THREE.InstancedMesh | null>(null);
  const [edgeMesh, setEdgeMesh] = useState<THREE.InstancedMesh | null>(null);
  const faceMaterialRef = useRef<THREE.MeshBasicMaterial | null>(null);
  const edgeMaterialRef = useRef<THREE.LineBasicMaterial | null>(null);
  const cuboidsRef = useRef<Cuboid[]>([]);
  const transitionRef = useRef<{ startTime: number; duration: number } | null>(null);

  // Create materials once
  useEffect(() => {
    faceMaterialRef.current = createCuboidFaceMaterial();
    edgeMaterialRef.current = createCuboidEdgeMaterial();

    return () => {
      faceMaterialRef.current?.dispose();
      edgeMaterialRef.current?.dispose();
      disposeSharedGeometries();
    };
  }, []);

  // Rebuild instances when frame changes
  useEffect(() => {
    if (!currentFrame || !faceMaterialRef.current || !edgeMaterialRef.current) return;

    const cuboids = currentFrame.getAllCuboids();
    cuboidsRef.current = cuboids;

    const newFace = buildFaceInstances(cuboids, faceMaterialRef.current);
    const newEdge = buildEdgeInstances(cuboids, edgeMaterialRef.current);

    setFaceMesh(prev => { prev?.dispose(); return newFace; });
    setEdgeMesh(prev => { prev?.dispose(); return newEdge; });

    // Only interpolate between adjacent frames (diff of 1)
    const isAdjacent = prevFrame && Math.abs(currentFrame.frameId - prevFrame.frameId) === 1;
    if (isAdjacent) {
      transitionRef.current = { startTime: performance.now(), duration: 200 };
    } else {
      transitionRef.current = null;
    }
  }, [currentFrame]);

  // Interpolation in render loop
  useFrame(() => {
    const transition = transitionRef.current;
    if (!transition || !prevFrame || !currentFrame || !faceMesh || !edgeMesh) return;

    const elapsed = performance.now() - transition.startTime;
    const t = Math.min(1, elapsed / transition.duration);

    if (t >= 1) {
      const cuboids = currentFrame.getAllCuboids();
      cuboidsRef.current = cuboids;
      updateInstanceMatrices(faceMesh, cuboids);
      updateInstanceMatrices(edgeMesh, cuboids);
      transitionRef.current = null;
      return;
    }

    const transitions = resolveTransition(prevFrame, currentFrame);
    const interpolated: Cuboid[] = [];

    for (const tr of transitions) {
      if (tr.state === 'matched' && tr.prev && tr.next) {
        interpolated.push(interpolate(tr.prev, tr.next, t));
      } else if (tr.state === 'entering' && tr.next) {
        interpolated.push(tr.next);
      }
    }

    cuboidsRef.current = interpolated;
    updateInstanceMatrices(faceMesh, interpolated);
    updateInstanceMatrices(edgeMesh, interpolated);
  });

  const handlePointerOver = useCallback((e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    const uuid = getInstanceUuid(e, cuboidsRef.current);
    if (uuid) onHover(uuid);
  }, [onHover]);

  const handlePointerOut = useCallback((e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    onUnhover();
  }, [onUnhover]);

  const handleClick = useCallback((e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    const uuid = getInstanceUuid(e, cuboidsRef.current);
    if (uuid) onPin(uuid);
  }, [onPin]);

  if (!faceMesh || !edgeMesh) return null;

  return (
    <group>
      <primitive
        object={faceMesh}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
        onClick={handleClick}
      />
      <primitive object={edgeMesh} />
    </group>
  );
}
